import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarRange,
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Plane,
  Plus,
  Printer,
  Sparkles,
  Ticket,
} from "lucide-react";
import { itineraryApi, tripPlanApi } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import type {
  ItemSegment,
  ItineraryItemType,
  ItineraryResponse,
  PlanLeg,
  StewardGap,
  TripPlanResponse,
} from "@/lib/types";
import ItineraryItemDialog from "@/components/trips/itinerary/ItineraryItemDialog";
import AddToItinerarySheet, {
  type AddRequest,
} from "@/components/trips/itinerary/AddToItinerarySheet";
import type { AddContext } from "@/components/trips/itinerary/addToItinerary-utils";
import { tripPlaces } from "@/components/trips/itinerary/tripPlaces";
import { planTargetFor } from "@/components/trips/completeness/completeness-plan";
import CompletenessRail from "@/components/trips/completeness/CompletenessRail";
import type { StewardSeed } from "@/components/trips/steward/StewardWorkspace";
import { invalidateAfterItineraryWrite } from "@/lib/itineraryCache";
import ItineraryDayCard from "./ItineraryDayCard";
import CruiseImportCard from "./CruiseImportCard";
import FlightStop from "./FlightStop";
import BookTripSheet from "./BookTripSheet";
import DidYouBookPrompt from "./DidYouBookPrompt";
import AirfareSummaryRail from "./AirfareSummaryRail";
import TripAdvisorPanel from "./TripAdvisorPanel";
import SourceLegend from "./SourceLegend";
import TravelerNames from "./TravelerNames";
import { dayHeading, itemsOf } from "./itineraryDay-utils";
import {
  buildTimeline,
  defaultOpenSectionId,
  sectionIdForDate,
  type TripSection,
} from "./tripSections";
import { pendingEventFor } from "./unconfirmedEvent";

/** A deep-link request to auto-open the add dialog (?addStop=1). */
export interface UnifiedAddStopRequest {
  date?: string;
  type?: ItineraryItemType;
}

interface TripTimelineProps {
  tripId: string;
  /** When set (email deep-link), auto-open the add dialog prefilled. */
  addStopRequest?: UnifiedAddStopRequest | null;
  onAddStopConsumed?: () => void;
  /** Open the Steward workspace, bare or with a seed. */
  onAskSteward?: (seed?: StewardSeed) => void;
  /** Steward's gap analysis; supplies the disembark city to the add sheet. */
  stewardGap?: StewardGap | null;
  /** The trip's own words, mined for an event nobody has confirmed yet. */
  description?: string | null;
  /**
   * Which flight's options to open on arrival (?leg=<routeId>, or "first" for
   * ?view=fares with no leg). The row scrolls into view once it exists.
   */
  openOptions?: string | null;
  /** The printed document: every section open, flights and rows read-only. */
  printMode?: boolean;
}

function localToday(): string {
  const n = new Date();
  const p = (x: number) => String(x).padStart(2, "0");
  return n.getFullYear() + "-" + p(n.getMonth() + 1) + "-" + p(n.getDate());
}

/** "2026-09-03" + "2026-09-11" -> "Sep 3 – Sep 11, 2026". Null-safe. */
function formatDateRange(start: string | null, end: string | null): string | null {
  const fmt = (iso: string, withYear: boolean) => {
    const d = new Date(iso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return iso;
    const opts: Intl.DateTimeFormatOptions = withYear
      ? { month: "short", day: "numeric", year: "numeric" }
      : { month: "short", day: "numeric" };
    return d.toLocaleDateString("en-US", opts);
  };
  if (start && end) return fmt(start, false) + " – " + fmt(end, true);
  if (start) return fmt(start, true);
  if (end) return fmt(end, true);
  return null;
}

/** "$2,260" — whole dollars; the headline never shows cents. */
function money(n: number): string {
  return "$" + Math.round(n).toLocaleString();
}

/**
 * The trip as ONE timeline.
 *
 * Flights are the backbone: each shows the chosen flight and one "Flight
 * options" control. Between them sit collapsible stretches (before you fly, the
 * stay, coming home) holding everything else, in the stretch its date falls in.
 *
 * There is deliberately one renderer: the printed page is this component in
 * `printMode`, so a print view cannot drift from the screen view.
 */
export default function TripTimeline({
  tripId,
  addStopRequest,
  onAddStopConsumed,
  onAskSteward,
  stewardGap,
  description,
  openOptions = null,
  printMode = false,
}: TripTimelineProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const itineraryQuery = useQuery<ItineraryResponse>({
    queryKey: ["itinerary", tripId],
    queryFn: () => itineraryApi.get(tripId),
    enabled: !!tripId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  const itinerary = itineraryQuery.data;

  // The trip plan is the source of truth for dates/fares and changes
  // server-side (date edits, re-searches, the scheduled sweep): always fresh.
  // Skipped in print — the printed page carries no fares.
  const { data: plan } = useQuery<TripPlanResponse>({
    queryKey: ["tripPlan", tripId],
    queryFn: () => tripPlanApi.get(tripId),
    enabled: !!tripId && !printMode,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const days = useMemo(() => itinerary?.days ?? [], [itinerary]);
  const entries = useMemo(
    () => buildTimeline(days, { destinationFallback: plan?.legs[0]?.destinationCity }),
    [days, plan],
  );
  const legByRoute = useMemo(() => {
    const map = new Map<string, PlanLeg>();
    for (const leg of plan?.legs ?? []) map.set(leg.routeId, leg);
    return map;
  }, [plan]);
  const dayIndex = useMemo(() => {
    const map = new Map<string, number>();
    days.forEach((d, i) => map.set(d.date, i));
    return map;
  }, [days]);
  const dayDates = days.map((d) => d.date);

  const pending = useMemo(() => {
    const flightDates = entries.flatMap((e) =>
      e.type === "flight" &&
      (e.flight.legRole === "outbound" || e.flight.legRole === "return") &&
      e.flight.departDate
        ? [e.flight.departDate]
        : [],
    );
    return pendingEventFor(days, description, flightDates);
  }, [days, entries, description]);

  const confirmPending = useMutation({
    mutationFn: () =>
      itineraryApi.createItem(tripId, {
        item_type: "event",
        title: pending!.title,
        day_date: pending!.date,
      }),
    onSuccess: () => invalidateAfterItineraryWrite(queryClient, tripId),
  });

  /* ── Open / shut state ─────────────────────────────────────────────────── */

  const [openSections, setOpenSections] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [closedDays, setClosedDays] = useState<string[]>([]);

  // Seed once from the content: the stretch holding today (or the next one) is
  // open. Re-seeding on every refetch would slam shut a stretch just opened.
  useEffect(() => {
    if (seeded || days.length === 0) return;
    const want = new Set<string>();
    const id = defaultOpenSectionId(entries, localToday());
    if (id) want.add(id);
    if (addStopRequest?.date) {
      const hit = sectionIdForDate(entries, addStopRequest.date);
      if (hit) want.add(hit);
    }
    if (pending) {
      const hit = sectionIdForDate(entries, pending.date);
      if (hit) want.add(hit);
    }
    setOpenSections([...want]);
    setSeeded(true);
  }, [days, entries, seeded, addStopRequest, pending]);

  const sections = entries.flatMap((e) => (e.type === "section" ? [e.section] : []));
  const allOpen = sections.length > 0 && sections.every((s) => openSections.includes(s.id));
  const toggleSection = (id: string) =>
    setOpenSections((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setOpenSections(allOpen ? [] : sections.map((s) => s.id));
  const toggleDay = (date: string) =>
    setClosedDays((prev) => (prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]));

  /* ── Add / edit / delete ───────────────────────────────────────────────── */

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ItemSegment | null>(null);
  const [defaultDate, setDefaultDate] = useState<string | undefined>();
  const [defaultType, setDefaultType] = useState<ItineraryItemType | undefined>();
  const [defaultCity, setDefaultCity] = useState<string | undefined>();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetContext, setSheetContext] = useState<AddContext>({});
  const [sheetChoice, setSheetChoice] = useState<string | null>(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [bookMode, setBookMode] = useState<"go" | "record">("go");

  const openAddSheet = (ctx: AddContext, choiceKey: string | null = null) => {
    setSheetContext(ctx);
    setSheetChoice(choiceKey);
    setSheetOpen(true);
  };

  /** The manual form, prefilled — reached from the sheet, or from a deep link. */
  const openForm = (request: AddRequest) => {
    setEditing(null);
    setDefaultDate(request.date ?? undefined);
    setDefaultCity(request.city ?? undefined);
    setDefaultType(request.itemType);
    setDialogOpen(true);
  };

  const openEdit = (seg: ItemSegment) => {
    setEditing(seg);
    setDefaultDate(seg.day_date);
    setDefaultCity(undefined);
    setDefaultType(undefined);
    setDialogOpen(true);
  };

  // A deep link (?addStop=1 from an email) goes STRAIGHT to the form: the link
  // already said what and when, and re-asking would be a step backwards.
  useEffect(() => {
    if (!addStopRequest || printMode) return;
    openForm({
      date: addStopRequest.date ?? null,
      city: null,
      itemType: addStopRequest.type ?? "activity",
    });
    onAddStopConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addStopRequest]);

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => itineraryApi.deleteItem(tripId, itemId),
    onSuccess: () => invalidateAfterItineraryWrite(queryClient, tripId),
  });
  const handleDelete = (seg: ItemSegment) => {
    if (window.confirm("Remove “" + seg.title + "” from the itinerary?")) {
      deleteMutation.mutate(seg.id);
    }
  };

  /* ── Deep link: open a flight's options and bring it into view ─────────── */

  const firstFlight = entries.find(
    (e): e is Extract<typeof e, { type: "flight" }> => e.type === "flight",
  );
  const firstRouteId = firstFlight?.flight.routeId ?? null;
  const optionsRouteId = openOptions === "first" ? firstRouteId : openOptions;
  useEffect(() => {
    if (!optionsRouteId || printMode) return;
    const timer = setTimeout(() => {
      document
        .getElementById("leg-" + optionsRouteId)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(timer);
  }, [optionsRouteId, printMode, entries.length]);

  /* ── Loading / error / empty ───────────────────────────────────────────── */

  if (itineraryQuery.isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="mt-2 h-3 w-56" />
          </div>
        ))}
      </div>
    );
  }

  if (itineraryQuery.isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card py-10 text-center">
        <AlertTriangle className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">We couldn&apos;t load your itinerary.</p>
        <Button variant="outline" size="sm" onClick={() => itineraryQuery.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card py-12 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <CalendarRange className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        </span>
        <div>
          <p className="text-sm font-medium">No itinerary yet</p>
          <p className="text-xs text-muted-foreground">
            Run a price check to find flights, or add the first thing you already booked.
          </p>
        </div>
        {!printMode && (
          <Button size="sm" onClick={() => openAddSheet({})}>
            <Plus className="mr-1.5 h-4 w-4" /> Add your first stop
          </Button>
        )}
      </div>
    );
  }

  /* ── The timeline ──────────────────────────────────────────────────────── */

  const partySize = plan?.trip.travelers ?? null;
  const travelers = plan?.travelers;
  const unbookedLegs = (plan?.legs ?? []).filter((l) => (l.bookingStatus ?? "none") === "none");
  const legsToAskAbout = unbookedLegs.filter((l) => l.askAboutBooking);

  const range =
    dayHeading(days[0].date).date +
    " – " +
    dayHeading(days[days.length - 1].date).date +
    " · " +
    days.length +
    (days.length === 1 ? " day" : " days");

  const timeline = (
    <div className="space-y-3">
      {entries.map((entry) => {
        if (entry.type === "flight") {
          const seg = entry.flight;
          return (
            <FlightStop
              key={"flight-" + seg.routeId}
              tripId={tripId}
              seg={seg}
              leg={legByRoute.get(seg.routeId)}
              partySize={partySize}
              travelers={travelers}
              readOnly={printMode}
              defaultOpen={!!optionsRouteId && optionsRouteId === seg.routeId}
            />
          );
        }
        const section = entry.section;
        if (printMode && section.days.every((d) => itemsOf(d).length === 0 && d.ongoing.length === 0)) {
          return null;
        }
        return (
          <SectionCard
            key={section.id}
            section={section}
            open={printMode || openSections.includes(section.id)}
            printMode={printMode}
            hasPending={!!pending && section.days.some((d) => d.date === pending.date)}
            onToggle={() => toggleSection(section.id)}
            onAdd={() =>
              openAddSheet({
                date: section.anchorDate,
                city: null,
                position: section.kind === "before" ? "before you fly" : section.kind === "after" ? "after you land" : null,
              })
            }
          >
            {section.days
              .filter((d) => itemsOf(d).length > 0 || pending?.date === d.date)
              .map((day) => (
                <ItineraryDayCard
                  key={day.date}
                  day={day}
                  index={dayIndex.get(day.date) ?? 0}
                  expanded={!closedDays.includes(day.date)}
                  onToggle={toggleDay}
                  onEditItem={openEdit}
                  onDeleteItem={handleDelete}
                  deletingId={deleteMutation.isPending ? (deleteMutation.variables as string) : null}
                  pendingEvent={pending && pending.date === day.date ? { title: pending.title } : null}
                  onConfirmPending={() => confirmPending.mutate()}
                  confirmingPending={confirmPending.isPending}
                  printMode={printMode}
                />
              ))}
          </SectionCard>
        );
      })}
    </div>
  );

  if (printMode) return <section className="space-y-3">{timeline}</section>;

  const total = plan?.total ?? null;
  const priceBasis = plan?.priceBasis ?? "quoted";
  const paidTotal = plan?.paidTotal ?? null;
  const quotedTotal = plan?.quotedTotal ?? null;
  const pax = partySize && partySize > 0 ? partySize : null;
  const perPerson = total != null && pax ? total / pax : null;
  const outboundLeg = plan?.legs.find((l) => l.role === "outbound") ?? plan?.legs[0];
  const subline = plan
    ? [
        formatDateRange(plan.trip.startDate, plan.trip.endDate),
        outboundLeg
          ? (outboundLeg.destinationCity || outboundLeg.destination) +
            (plan.legs.some((l) => l.role === "return") ? " round-trip" : " one-way")
          : null,
        partySize != null ? partySize + (partySize === 1 ? " traveler" : " travelers") : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="space-y-4">
      {plan && (
        <div className="rounded-xl border bg-card px-4 py-3 shadow-paper">
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
            <div className="min-w-0 space-y-1">
              {subline && <p className="text-sm text-muted-foreground">{subline}</p>}
              <TravelerNames travelers={plan.travelers} tripId={tripId} passengers={plan.trip.travelers} />
            </div>
            {/* The trip total, on top and labelled — a register requirement,
                and the first regression docs/REQUIREMENTS.md exists to stop. */}
            <div className="shrink-0 text-right lg:hidden">
              {total != null ? (
                <>
                  <p className="text-lg font-medium leading-tight tabular-nums text-foreground">
                    {money(total)} <span className="text-sm font-normal text-muted-foreground">total</span>
                  </p>
                  {perPerson != null && (
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {money(perPerson)} per person
                    </p>
                  )}
                  {priceBasis === "paid" && (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
                      <Check className="h-3 w-3" aria-hidden="true" /> What you paid
                    </p>
                  )}
                  {priceBasis === "mixed" && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {paidTotal != null && (
                        <>
                          <span className="tabular-nums text-emerald-700">{money(paidTotal)}</span> paid
                        </>
                      )}
                      {paidTotal != null && quotedTotal != null && " · "}
                      {quotedTotal != null && (
                        <>
                          <span className="tabular-nums">{money(quotedTotal)}</span> still a live fare
                        </>
                      )}
                    </p>
                  )}
                  {priceBasis === "quoted" && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">Live fare, not booked</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Pricing your trip…</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        {/* The timeline first, on every width: on a phone the first flight must
            be on the first screen, not the airfare box. */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{range}</p>
            <div className="flex shrink-0 items-center gap-1 print:hidden">
              <Button variant="ghost" size="sm" onClick={toggleAll}>
                {allOpen ? (
                  <ChevronsDownUp className="mr-1.5 h-4 w-4" />
                ) : (
                  <ChevronsUpDown className="mr-1.5 h-4 w-4" />
                )}
                {allOpen ? "Collapse all" : "Expand all"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/trips/" + tripId + "/print")}
              >
                <Printer className="mr-1.5 h-4 w-4" /> Print
              </Button>
            </div>
          </div>

          {itinerary?.cruiseImportAvailable && (
            <CruiseImportCard tripId={tripId} linkKnown={!!itinerary.cruiseLinkKnown} />
          )}

          {entries.every((e) => e.type === "section") && plan && plan.legs.length === 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-dashed bg-card px-3 py-3 text-sm text-muted-foreground">
              <Plane className="h-4 w-4" aria-hidden="true" />
              No flights yet. Run a price check to start building your trip.
            </div>
          )}

          {timeline}

          {onAskSteward && (
            <button
              type="button"
              onClick={() => onAskSteward()}
              className="flex w-full items-center gap-2 rounded-lg border border-dashed border-[hsl(var(--primary))]/40 bg-card px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-[hsl(var(--primary))]/[0.05] motion-reduce:transition-none"
            >
              <Sparkles className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]" aria-hidden="true" />
              <span className="flex-1">Not sure what to add? Plan it with Steward</span>
              <span className="text-xs text-[hsl(var(--primary))]">Ask</span>
            </button>
          )}

          <SourceLegend entries={plan?.sourceLegend ?? []} />
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          {legsToAskAbout.length > 0 && (
            <DidYouBookPrompt
              tripId={tripId}
              legs={legsToAskAbout}
              onRecord={() => {
                setBookMode("record");
                setBookOpen(true);
              }}
            />
          )}

          {plan && (total != null || unbookedLegs.length > 0) && (
            <div className="rounded-xl border bg-card p-4 shadow-paper">
              <AirfareSummaryRail
                airfare={total}
                breakdown={plan.costBreakdown}
                travelers={plan.trip.travelers}
                priceBasis={priceBasis}
                bare
                figureLaptopOnly
              />
              {unbookedLegs.length > 0 && (
                <div className={total != null ? "lg:mt-3 lg:border-t lg:pt-3" : ""}>
                  <Button
                    className="w-full"
                    onClick={() => {
                      setBookMode("go");
                      setBookOpen(true);
                    }}
                  >
                    <Ticket className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    <span>
                      Book trip
                      {unbookedLegs.length > 1 && (
                        <>
                          {" · "}
                          <span className="tabular-nums">{unbookedLegs.length}</span>
                          {" flights"}
                        </>
                      )}
                    </span>
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setBookMode("record");
                      setBookOpen(true);
                    }}
                    className="mt-2 w-full text-center text-xs font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
                  >
                    Already booked? Record what you paid
                  </button>
                </div>
              )}
            </div>
          )}
          {plan?.advisor && (
            <div className="rounded-xl border bg-card p-4 shadow-paper">
              <TripAdvisorPanel advisor={plan.advisor} />
            </div>
          )}
          {plan && (
            <CompletenessRail
              tripId={tripId}
              itinerary={itinerary}
              onPlan={(el) => {
                const t = planTargetFor(el, plan);
                openAddSheet(t.context, t.choiceKey);
              }}
            />
          )}
        </aside>
      </div>

      <AddToItinerarySheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        context={sheetContext}
        dayDates={dayDates}
        defaultChoiceKey={sheetChoice}
        places={tripPlaces({
          slotCity: sheetContext.city,
          plan: plan ?? null,
          itinerary,
          gap: stewardGap,
        })}
        onAskSteward={(seed) => onAskSteward?.(seed)}
        onEnterManually={openForm}
      />

      <ItineraryItemDialog
        tripId={tripId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        dayDates={dayDates}
        defaultDate={defaultDate}
        defaultType={defaultType}
        defaultCity={defaultCity}
        companions={itinerary?.companions ?? []}
        onSaved={() => invalidateAfterItineraryWrite(queryClient, tripId)}
      />

      {plan && (
        <BookTripSheet
          open={bookOpen}
          onOpenChange={setBookOpen}
          tripId={tripId}
          legs={unbookedLegs}
          passengers={plan.trip.travelers}
          travelers={plan.travelers}
          initialMode={bookMode}
        />
      )}
    </div>
  );
}

/** One collapsible stretch between flights. */
function SectionCard({
  section,
  open,
  printMode,
  hasPending,
  onToggle,
  onAdd,
  children,
}: {
  section: TripSection;
  open: boolean;
  printMode: boolean;
  /** A proposed (unconfirmed) event lands in this stretch. */
  hasPending: boolean;
  onToggle: () => void;
  onAdd: () => void;
  children: ReactNode;
}) {
  const items = section.days.reduce((n, d) => n + itemsOf(d).length, 0);
  const summary =
    items === 0
      ? hasPending
        ? "1 unconfirmed event"
        : "Nothing planned"
      : items + (items === 1 ? " thing planned" : " things planned") + (hasPending ? " · 1 unconfirmed" : "");
  const bodyId = "section-body-" + section.id;

  const heading = (
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-foreground">{section.title}</span>
      <span className="block text-xs text-muted-foreground">{summary}</span>
    </span>
  );

  return (
    <section className="trip-section overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {printMode ? (
        <div className="px-3 py-3 sm:px-4">{heading}</div>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex min-h-[52px] w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/40 motion-reduce:transition-none sm:px-4"
        >
          {heading}
          <ChevronDown
            className={
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none " +
              (open ? "rotate-180" : "")
            }
            aria-hidden="true"
          />
        </button>
      )}
      {open && (
        <div id={bodyId} className="space-y-2 border-t border-border px-3 py-3 sm:px-4">
          {children}
          {!printMode && (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex min-h-[36px] w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/40 hover:text-foreground motion-reduce:transition-none print:hidden"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add to this part of the trip
            </button>
          )}
        </div>
      )}
    </section>
  );
}
